import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// Felső, csoport nélküli elemek
const TOP = [
  { to: '/', label: 'Áttekintés', icon: '📊' },
  { to: '/analytics', label: 'Analitika', icon: '📈' },
  { to: '/monthly-trends', label: 'Havi trendek', icon: '📅' },
];

// Csoportosított menü
const GROUPS = [
  {
    key: 'money', label: 'Pénzügy', icon: '💰',
    items: [
      { to: '/revenue', label: 'Bevételek', icon: '💰' },
      { to: '/costs', label: 'Költségek', icon: '💸' },
      { to: '/api-credits', label: 'API Kreditek', icon: '💳' },
      { to: '/promo', label: 'Promo kódok', icon: '🎟️' },
      { to: '/finance', label: 'Pénzügy (pályázat)', icon: '💶' },
      { to: '/eu-grant', label: 'Pályázati adatlap', icon: '🇪🇺' },
    ],
  },
  {
    key: 'users', label: 'Felhasználók', icon: '👥',
    items: [
      { to: '/registrations', label: 'Regisztrációk', icon: '🧑‍💻' },
      { to: '/user-activity', label: 'User Activity', icon: '👤' },
      { to: '/poi-usage', label: 'POI használat', icon: '🧭' },
      { to: '/activity-map', label: 'Activity Map', icon: '🌍' },
      { to: '/trips', label: 'Utazások', icon: '🧳' },
      { to: '/comped', label: 'Comped', icon: '🎁' },
    ],
  },
  {
    key: 'partners', label: 'Partnerek', icon: '🤝',
    items: [
      { to: '/affiliates', label: 'Affiliates', icon: '🤝' },
      { to: '/affiliate-performance', label: 'Partner-teljesítmény', icon: '📈' },
    ],
  },
  {
    key: 'content', label: 'Tartalom', icon: '🗣️',
    items: [
      { to: '/narrations', label: 'Narrációk', icon: '🗣️' },
      { to: '/quality', label: 'Minőség', icon: '🧪' },
      { to: '/poi-quality', label: 'POI minőség', icon: '🏆' },
      { to: '/custom-pois', label: 'Custom POIs', icon: '📍' },
      { to: '/tts', label: 'TTS Stúdió', icon: '🎙️' },
    ],
  },
  {
    key: 'marketing', label: 'Marketing', icon: '📣',
    items: [
      { to: '/reviews', label: 'Értékelések', icon: '📱' },
      { to: '/push-stats', label: 'Push Stats', icon: '🔔' },
      { to: '/push-send', label: 'Push küldés', icon: '🔔' },
      { to: '/business-intel', label: 'Business Intel', icon: '💼' },
    ],
  },
  {
    key: 'system', label: 'Rendszer', icon: '🖥️',
    items: [
      { to: '/server', label: 'Szerver', icon: '🖥️' },
      { to: '/server-pro', label: 'Szerver Pro', icon: '🩺' },
      { to: '/gnss', label: 'GNSS / Galileo', icon: '🛰️' },
      { to: '/logs', label: 'Logok', icon: '📋' },
      { to: '/system-events', label: 'Esemény napló', icon: '📜' },
      { to: '/admin', label: 'Admin', icon: '⚙️' },
    ],
  },
];

const groupOfPath = (path) => {
  for (const g of GROUPS) if (g.items.some((i) => i.to === path)) return g.key;
  return null;
};

export default function Layout({ user, onLogout, children }) {
  const [open, setOpen] = useState(false);
  const [qualityAlert, setQualityAlert] = useState(false);
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() => {
    const g = groupOfPath(window.location.pathname);
    return g ? { [g]: true } : {};
  });

  useEffect(() => {
    const g = groupOfPath(location.pathname);
    if (g) setOpenGroups((prev) => (prev[g] ? prev : { ...prev, [g]: true }));
  }, [location.pathname]);

  useEffect(() => {
    fetch('/api/quality/report', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setQualityAlert(!!(j && j.report && j.report.alert)))
      .catch(() => {});
  }, []);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const itemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8,
    color: isActive ? '#00d4ff' : '#8b8fa3',
    background: isActive ? 'rgba(0,212,255,0.1)' : 'transparent',
    fontWeight: isActive ? 600 : 400, fontSize: 14, textDecoration: 'none', flexShrink: 0,
  });

  const renderItem = (n, indent) => (
    <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)}
      style={({ isActive }) => ({ ...itemStyle(isActive), paddingLeft: indent ? 28 : 12 })}>
      <span>{n.icon}</span> {n.label}
      {n.to === '/quality' && qualityAlert && <span style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: 4, background: '#f43f5e', flexShrink: 0 }} />}
    </NavLink>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <div id="mobile-header" style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, height: 50, background: '#1a1d27', borderBottom: '1px solid #2a2e3a', alignItems: 'center', padding: '0 12px', zIndex: 1001, gap: 12 }}>
        <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', color: '#00d4ff', fontSize: 24, cursor: 'pointer' }}>☰</button>
        <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: 16 }}>🗺️ AI TourMate</span>
      </div>
      {open && <div id="overlay" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'none' }} />}
      <aside id="sidebar" className={open ? 'open' : ''} style={{ width: 220, maxHeight: '100vh', background: '#1a1d27', borderRight: '1px solid #2a2e3a', display: 'flex', flexDirection: 'column', padding: '20px 0', flexShrink: 0, zIndex: 1002 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#00d4ff', padding: '0 20px 20px', borderBottom: '1px solid #2a2e3a', flexShrink: 0 }}>🗺️ AI TourMate</div>
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 8px', overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          {TOP.map((n) => renderItem(n, false))}
          {GROUPS.map((g) => {
            const opened = !!openGroups[g.key];
            const activeInGroup = g.items.some((i) => i.to === location.pathname);
            const groupAlert = g.key === 'content' && qualityAlert;
            return (
              <div key={g.key} style={{ marginTop: 6 }}>
                <div onClick={() => toggleGroup(g.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: activeInGroup ? '#00d4ff' : '#8b8fa3' }}>
                  <span>{g.icon}</span> {g.label}
                  {groupAlert && !opened && <span style={{ width: 8, height: 8, borderRadius: 4, background: '#f43f5e', flexShrink: 0 }} />}
                  <span style={{ marginLeft: 'auto', fontSize: 10 }}>{opened ? '▲' : '▼'}</span>
                </div>
                {opened && g.items.map((n) => renderItem(n, true))}
              </div>
            );
          })}
        </nav>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #2a2e3a', flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: '#8b8fa3', marginBottom: 8 }}>{user?.username}</div>
          <button onClick={onLogout} style={{ background: 'none', border: '1px solid #2a2e3a', color: '#8b8fa3', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, width: '100%' }}>Kijelentkezés</button>
        </div>
      </aside>
      <main id="main-content" style={{ flex: 1, padding: 24, overflowY: 'auto' }}>{children}</main>
    </div>
  );
}
